import { SocialMediaServiceEnum } from '../../api/client'

/**
 * The platforms scheduled scraping can drive, and everything the scheduling
 * UI needs to know about each one.
 *
 * There are two spellings of a platform in play and they are not
 * interchangeable, which is why both live on the same row rather than being
 * derived from one another. `SocialMediaServiceEnum` is the GraphQL enum and
 * serialises uppercase (`INSTAGRAM`); the scheduler's own arguments are the
 * lowercase `socialMediaServiceShortName` (`instagram`), because the backend
 * parses them with `SocialMediaServiceName`, whose `FromStr` is lowercase-only.
 * Send the enum where a wire value belongs and creation fails with "unknown
 * platform 'INSTAGRAM'".
 *
 * Shared rather than page-local: the create form and the account settings card
 * both need the wire value, the display name and whether whole-profile mode
 * works, and two copies of that would only agree until someone added a
 * platform.
 */
export type PlatformConfig = {
    label: string
    /** The lowercase platform string the create mutation parses. */
    wireValue: string
    /** The SocialMediaServiceEnum value searchAccounts filters on. */
    searchEnum: SocialMediaServiceEnum
    /** The :platform key of the New Monitored Account route. */
    monitoredRouteKey: string
    /**
     * Whether whole-profile mode is supported. YouTube's is not: a channel with
     * Fetch with API on is polled for uploads and its comments collected by the
     * rotation, so a whole-profile schedule would have nothing to add.
     */
    supportsProfileMode: boolean
    /** Whether specific-posts mode is supported (Facebook's is not). */
    supportsPostsMode: boolean
    /**
     * Whether specific-posts mode needs a start date.
     *
     * A capability rather than a `wireValue === 'youtube'` check at each of
     * the three places that asked: YouTube's targeted fetch always collects
     * each video's whole comment set, so it takes no window and the backend
     * rejects one. The next platform that behaves that way should be a row
     * in this table, not another special case in the form.
     */
    postsModeNeedsSinceDate: boolean
    targetsLabel: string
    targetsHint: string
}

export const PLATFORMS: PlatformConfig[] = [
    {
        label: 'Instagram',
        wireValue: 'instagram',
        searchEnum: SocialMediaServiceEnum.Instagram,
        monitoredRouteKey: 'instagram',
        supportsProfileMode: true,
        supportsPostsMode: true,
        postsModeNeedsSinceDate: true,
        targetsLabel: 'Post URLs',
        targetsHint: 'One post URL per line, e.g. https://www.instagram.com/p/DEf5gHi/'
    },
    {
        label: 'Facebook',
        wireValue: 'facebook',
        searchEnum: SocialMediaServiceEnum.Facebook,
        monitoredRouteKey: 'facebook',
        supportsProfileMode: true,
        // The scrape facade doesn't route targeted Facebook comment
        // scraping, so the backend rejects posts mode for it.
        supportsPostsMode: false,
        postsModeNeedsSinceDate: true,
        targetsLabel: 'Post URLs',
        targetsHint: 'One post URL per line'
    },
    {
        label: 'TikTok',
        wireValue: 'tiktokuser',
        searchEnum: SocialMediaServiceEnum.Tiktokuser,
        monitoredRouteKey: 'tiktok_user',
        supportsProfileMode: true,
        supportsPostsMode: true,
        postsModeNeedsSinceDate: true,
        targetsLabel: 'Post URLs',
        targetsHint:
            'One post URL per line, e.g. https://www.tiktok.com/@handle/video/7234567890123456789'
    },
    {
        label: 'YouTube',
        wireValue: 'youtube',
        searchEnum: SocialMediaServiceEnum.Youtube,
        monitoredRouteKey: 'youtube',
        // Whole-profile collection of a YouTube channel is the upload poller's
        // job once Fetch with API is on; schedules only support the targeted
        // (specific videos) path, for a channel with the switch off.
        supportsProfileMode: false,
        supportsPostsMode: true,
        // The targeted fetch takes each video's whole comment set.
        postsModeNeedsSinceDate: false,
        targetsLabel: 'Video IDs',
        targetsHint: 'One video id per line — the 11-character id, e.g. dQw4w9WgXcQ'
    }
]

/**
 * The entry for an account's `socialMediaServiceShortName`, or `undefined` for
 * a platform no schedule can be created for — Twitter, say, which the scrape
 * facade does not route.
 *
 * Case-insensitive so callers can hand over whatever the account gives them:
 * the short name is the wire value, and getting it to lowercase is this
 * module's job rather than every call site's.
 */
export function platformFor(shortName: string): PlatformConfig | undefined {
    const wanted = shortName.toLowerCase()
    return PLATFORMS.find((platform) => platform.wireValue === wanted)
}
